import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import s from './CompanionConfigVersion.module.scss';
import { FormattedMessage, injectIntl } from 'react-intl';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { initialCompanionConfigVersionState } from '../../reducers/initialState';
import { v4 as uuidv4 } from 'uuid';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import Banner from '../Banner/Banner';
import { Navigate, useParams, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { isRequired } from '../../utils';
import Cookies from 'js-cookie';
import FileUploader from '../WSAControls/FileUploader/FileUploader';
import FileList from '../WSAControls/FileList/FileList';
import Breadcrumb from '../WSAControls/Breadcrumb/BreadCrumb';
import ModelConfigurationContentTemplate from '../ModelTab/ModelConfigurationContentTemplate';
import { IsWidgetAccessible } from '../../utils/widgetManager';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import help from '../../assets/help-grey.svg';
import uploadFileIcon from '../../assets/cloud-upload-file-grey.svg';

// major.minor.patch, digits only (e.g. 1.0.0) - matches the format the
// tooltip next to the Version field describes.
const VERSION_FORMAT_REGEX = /^\d+\.\d+\.\d+$/;

// Shared with both the real FileUploader instance and the disabled
// placeholder's own "Accepted file formats" line, so the two can never
// drift apart.
const ALLOWED_FILE_TYPES = [['.zip', 'zip']];

// BCC-18: add/edit/publish screen for a companion app config version - one
// component serves both add and edit, exactly like ModelFirmwareVersion.jsx
// does (branching on whether :ModelCompanionConfigVersionId is present in
// the route). Mirrors that component's two-step upload-then-save flow:
// FileUploader uploads the zip to S3 on its own; only clicking this form's
// own Save button POSTs the metadata (incl. the resulting S3 key) and
// actually creates/updates the row. Success is only declared once that save
// call succeeds, never off the upload alone - see the plan's "the version
// row must exist too" note. Delete lives on the list screen instead (same
// place ModelSetpointsList.jsx puts its ConfirmDialog-driven delete), not
// duplicated here.
const CompanionConfigVersion = ({
  saveCompanionConfigVersion,
  describeCompanionConfigVersion,
  changeCompanionConfigVersion,
  resetCompanionConfigVersionStatus,
  companionConfigVersionManager = { ...initialCompanionConfigVersionState.companionConfigVersionManager },
  modelManager,
  userProfileWidgets,
  intl
}) => {
  const envName = process.env.REACT_APP_ENV_NAME_SHORT;
  let { ModelId, ModelCategoryId, ModelCompanionConfigVersionId } = useParams();
  // useParams() always returns strings - send ModelId as a number so it
  // matches the type the backend's DB-sourced comparisons expect (a raw
  // string here caused the delete "ownership" check to fail every time via
  // a strict !== against a numeric DB value).
  ModelId = parseInt(ModelId, 10);
  const navigate = useNavigate();

  const IsEdit = !!ModelCompanionConfigVersionId;
  const widgetCode = IsEdit ? 'COMPANIONCONFIGVERSIONUPDATE' : 'COMPANIONCONFIGVERSIONADD';

  const [localformErrors, setFormErrors] = useState({});
  const [saveGuid] = useState(uuidv4());
  const [isUploading, setIsUploading] = useState(false);
  const [attachedFileName, setAttachedFileName] = useState('');
  // Gates the auto-<Navigate> below so it only ever fires because of a save
  // THIS mount performed, never because of a stale isOpSuccessful/showBanner
  // left in the shared companionConfigVersionManager slice by some other
  // action (the list's delete banner, or a previous mount of this same
  // form) - see the long comment on the Navigate check further down for why
  // resetting that shared state on unmount/elsewhere isn't reliable enough
  // on its own. Always starts false on a fresh mount, so a leftover global
  // flag can never trigger an immediate redirect before the user sees the
  // form.
  const [justSaved, setJustSaved] = useState(false);

  const returnUrl = `/operational-setup/model-category-configuration/${ModelCategoryId}/model-configuration/${ModelId}/companion-config-version-list`;

  useEffect(() => {
    if (IsEdit && ModelCompanionConfigVersionId) {
      describeCompanionConfigVersion(ModelCompanionConfigVersionId, widgetCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [IsEdit, ModelCompanionConfigVersionId]);

  // Warn before the browser tab is closed/refreshed mid-upload - there is
  // no draft-recovery, so an interrupted upload just has to be redone; this
  // is the best we can do to stop it happening by accident.
  useEffect(() => {
    window.onbeforeunload = isUploading ? () => true : null;
    return () => {
      window.onbeforeunload = null;
    };
  }, [isUploading]);

  // Cleanup on unmount. Both resets matter: without resetCompanionConfigVersionStatus,
  // a stale isOpSuccessful: true from this save would immediately <Navigate>
  // the *next* time this form mounts (Add New, or Edit on any row) - see
  // that action's comment for the full explanation.
  useEffect(() => {
    return () => {
      changeCompanionConfigVersion({ ...initialCompanionConfigVersionState.companionConfigVersionManager.selectedCompanionConfigVersion });
      resetCompanionConfigVersionStatus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setLocalCompanionConfigVersion = selected => {
    changeCompanionConfigVersion(selected);
  };

  const onChange = e => {
    setLocalCompanionConfigVersion({
      ...companionConfigVersionManager.selectedCompanionConfigVersion,
      [e.target.name]: e.target.value
    });
  };

  const validateInput = () => {
    let formErrors = {};
    let isValid = true;
    let selected = companionConfigVersionManager.selectedCompanionConfigVersion;

    if (!selected || !selected.Name || isRequired(selected.Name, 1)) {
      formErrors.Name = intl.formatMessage({
        id: 'companionConfigVersion.nameMandatory',
        defaultMessage: 'Name is a mandatory field'
      });
      isValid = false;
    }

    if (!selected || !selected.VersionNumber || isRequired(selected.VersionNumber, 1)) {
      formErrors.VersionNumber = intl.formatMessage({
        id: 'companionConfigVersion.versionNumberMandatory',
        defaultMessage: 'Version number is a mandatory field'
      });
      isValid = false;
    } else if (!VERSION_FORMAT_REGEX.test(selected.VersionNumber)) {
      formErrors.VersionNumber = intl.formatMessage({
        id: 'companionConfigVersion.versionFormatInvalid',
        defaultMessage: 'Version must be in the format major.minor.patch, e.g. 1.0.0'
      });
      isValid = false;
    }

    if (!selected || !selected.ConfigFileS3Key) {
      formErrors.ConfigFileS3Key = intl.formatMessage({
        id: 'companionConfigVersion.fileMandatory',
        defaultMessage: 'Please attach a zip file'
      });
      isValid = false;
    }

    setFormErrors(formErrors);
    return isValid;
  };

  const submitForm = e => {
    e.preventDefault();

    if (!validateInput()) {
      return;
    }

    let saveData = { ...companionConfigVersionManager.selectedCompanionConfigVersion };
    saveData.ModelId = ModelId;
    saveData.UpdatedBy = Cookies.get(`userid-${envName}`) || 0;
    saveData.OrganisationId = Cookies.get(`selectedorganisationid-${envName}`) || 1;

    if (!IsEdit) {
      saveData.ModelCompanionConfigVersionId = 0;
      saveData.GuidIdentifier = saveGuid;
    }

    saveCompanionConfigVersion(saveData, widgetCode);
    setJustSaved(true);
  };

  const onPublishClick = () => {
    let saveData = { ...companionConfigVersionManager.selectedCompanionConfigVersion };
    saveData.Publish = true;
    saveData.UpdatedBy = Cookies.get(`userid-${envName}`) || 0;
    saveCompanionConfigVersion(saveData, 'COMPANIONCONFIGVERSIONPUBLISH');
    setJustSaved(true);
  };

  // FILE UPLOAD
  const onUploadStart = () => {
    setIsUploading(true);
  };

  // The actual byte transfer is done here (FileUploader's "Upload
  // Successful"/COMPLETE screen is now showing) - onUploadComplete below
  // only fires later, once the user clicks that COMPLETE button, which left
  // the "do not close this tab or navigate away" warning up for the whole
  // time that screen sat there unacknowledged.
  const onUploadFinished = () => {
    setIsUploading(false);
  };

  const onUploadComplete = data => {
    setIsUploading(false);
    if (data && data.length > 0) {
      let uploaded = data[0];
      setAttachedFileName(uploaded.name);
      setLocalCompanionConfigVersion({
        ...companionConfigVersionManager.selectedCompanionConfigVersion,
        ConfigFileS3Key: uploaded.S3URL,
        // The original filename - ConfigFileS3Key alone can't recover this
        // later (it's just `${location}/${fileGuid}`, a random UUID). Saved
        // to the backend's ConfigFileName column so a future Edit reload can
        // show a real name instead of falling back to that UUID.
        ConfigFileName: uploaded.name
      });
    }
  };

  // Clears the attachment locally only - nothing is deleted server-side
  // until Save is clicked, same as removing a not-yet-saved file does on
  // firmware's own FileList. validateInput already requires ConfigFileS3Key,
  // so a Save with no re-attached file correctly gets blocked with the
  // "please attach a zip file" error rather than silently dropping it.
  const onRemoveAttachedFile = () => {
    setAttachedFileName('');
    setLocalCompanionConfigVersion({
      ...companionConfigVersionManager.selectedCompanionConfigVersion,
      ConfigFileS3Key: '',
      ConfigFileName: ''
    });
  };
  // END FILE UPLOAD

  const onCancelClick = e => {
    e.preventDefault();
    if (isUploading) {
      const confirmed = window.confirm(
        intl.formatMessage({
          id: 'companionConfigVersion.confirmNavigateAwayWhileUploading',
          defaultMessage: 'An upload is still in progress. Leave anyway?'
        })
      );
      if (!confirmed) {
        return;
      }
    }
    navigate(returnUrl);
  };

  const messageId = (companionConfigVersionManager && companionConfigVersionManager.displayMessageCode) || 'none';
  const messageText = intl.formatMessage({ id: messageId, defaultMessage: messageId });
  const isDuplicateVersionError = companionConfigVersionManager.displayMessageCode === 'companionConfigVersion.duplicateVersion';

  const modelName = (modelManager.selectedModel ? modelManager.selectedModel.Name : '') || '';

  const headingId = IsEdit ? 'companionConfigVersion.editCompanionConfigVersion' : 'companionConfigVersion.addCompanionConfigVersion';
  const headingDefault = IsEdit ? 'Edit Companion App Config Version' : 'Add Companion App Config Version';

  const breadcrumbItems = [
    { label: <FormattedMessage id="modelcategory.modelcategory" defaultMessage="Model Category" />, href: '/operational-setup/model-category-list' },
    { label: <FormattedMessage id="model.models" defaultMessage="Models" />, href: `/operational-setup/model-category-configuration/${ModelCategoryId}/model-list` },
    { label: <FormattedMessage id="model.manageCompanionConfigVersions" defaultMessage="Companion App Config Versions" />, href: returnUrl },
    { label: <FormattedMessage id={headingId} defaultMessage={headingDefault} /> }
  ];

  const selected = companionConfigVersionManager.selectedCompanionConfigVersion;
  const isPublished = selected.Status === 'Published';
  // ConfigFileName is the real stored filename (set on upload, persisted by
  // the backend). Rows saved before that column existed won't have one -
  // fall back to the S3 key's last path segment (a raw UUID, not a real
  // name, but better than showing nothing).
  const displayFileName = selected.ConfigFileName || (selected.ConfigFileS3Key ? selected.ConfigFileS3Key.split('/').pop() : '');
  // FileList's downloadFile (WSAControls/FileList/FileList.jsx) looks a file
  // up by the client-generated fileGuid used when it was uploaded, not the
  // backend's own UploadedFileId - and ConfigFileS3Key is built as
  // `${location}/${fileGuid}` (see FileUploader.jsx), so that guid is
  // already recoverable as the S3 key's last path segment without needing
  // a dedicated column for it.
  const currentFileGuid = selected.ConfigFileS3Key ? selected.ConfigFileS3Key.split('/').pop() : '';

  return (
    <div className={s.companionConfigVersion}>
      {companionConfigVersionManager.isLoading && <LoadingSpinner />}
      {/* justSaved gates this on a save THIS mount performed (see its
          declaration above) - isOpSuccessful/showBanner alone aren't
          trustworthy here because they're the same two fields the list
          screen's delete banner uses, in the same shared
          companionConfigVersionManager slice. Resetting them on the list's
          unmount (previously the only fix here) isn't reliable: that reset
          and this form's very first mount/render happen in the SAME
          route-change commit, so this component's first render can still
          read the pre-reset, stale isOpSuccessful: true / showBanner: false
          left by the delete and redirect immediately - which is exactly why
          Add New/Edit needed a second click after deleting something. */}
      {justSaved && companionConfigVersionManager.isOpSuccessful && !companionConfigVersionManager.showBanner ? (
        <Navigate to={returnUrl} />
      ) : (
        ''
      )}
      <Banner
        failureText={messageText}
        showBanner={companionConfigVersionManager.showBanner}
        status={companionConfigVersionManager.isOpSuccessful}
        successText={messageText}
      />

      <ModelConfigurationContentTemplate selectedPage="manageCompanionConfigVersions" userProfileWidgets={userProfileWidgets} intl={intl}>
        <div className={s.contentWrapper}>
          <Row>
            <Col xs={12} md={6}>
              <div className="pb-3">
                <Breadcrumb items={breadcrumbItems} />
              </div>
              <h3 data-unittest="headingLabel">
                <FormattedMessage id={headingId} defaultMessage={headingDefault} /> {modelName ? ` - ${modelName}` : ''}
              </h3>
            </Col>
            <Col xs={12} md={6} className={s.alignRight}>
              {IsEdit && !isPublished && IsWidgetAccessible(userProfileWidgets, 'COMPANIONCONFIGVERSIONPUBLISH') && (
                <Button variant="outline-secondary" onClick={onPublishClick} data-unittest="publishData">
                  <FormattedMessage id="companionConfigVersion.publishVersion" defaultMessage="PUBLISH VERSION" />
                </Button>
              )}
            </Col>
          </Row>

          <div className={s.formContent}>
            <Form>
              <Row className={s.formRow}>
                <Col xs={12} lg={6}>
                  <Form.Group controlId="formCompanionConfigVersionName">
                    <Form.Label className={s.fieldLabel}>
                      <FormattedMessage id="companionConfigVersion.name" defaultMessage="Name" />
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="Name"
                      onChange={onChange}
                      value={selected.Name}
                      className={`${s.formControl} ${localformErrors && localformErrors.Name ? s.formControlError : ''}`}
                    />
                    {localformErrors && localformErrors.Name && (
                      <p role="alert" className={s.error}>
                        {localformErrors.Name}
                      </p>
                    )}
                  </Form.Group>
                </Col>
              </Row>
              <Row className={s.formRow}>
                <Col xs={12} lg={6}>
                  <Form.Group controlId="formCompanionConfigVersionNumber">
                    <Form.Label className={s.fieldLabel}>
                      <FormattedMessage id="companionConfigVersion.version" defaultMessage="Version" />
                      <img
                        src={help}
                        alt="info"
                        className={s.infoIcon}
                        onClick={e => e.preventDefault()}
                        data-tooltip-id="companionConfigVersionTooltip"
                        data-tooltip-content={intl.formatMessage({
                          id: 'companionConfigVersion.versionFormatHint',
                          defaultMessage: 'Must be in the format major.minor.patch, e.g. 1.0.0'
                        })}
                      />
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="VersionNumber"
                      onChange={onChange}
                      value={selected.VersionNumber}
                      placeholder="1.0.0"
                      className={`${s.formControl} ${localformErrors && localformErrors.VersionNumber ? s.formControlError : ''}`}
                    />
                    {localformErrors && localformErrors.VersionNumber && (
                      <p role="alert" className={s.error}>
                        {localformErrors.VersionNumber}
                      </p>
                    )}
                    {isDuplicateVersionError && (
                      <p role="alert" className={s.error}>
                        {messageText}
                      </p>
                    )}
                    <Tooltip id="companionConfigVersionTooltip" className="companionConfigVersionTooltip" />
                  </Form.Group>
                </Col>
              </Row>
              {isPublished && (
                <Row className={classNames(s.publishedSummary, s.formRow)}>
                  <Col xs={12} sm={2}>
                    <Form.Label className={s.fieldLabel}>
                      <FormattedMessage id="companionConfigVersion.publishedBy" defaultMessage="Published By" />
                    </Form.Label>
                  </Col>
                  <Col xs={12} sm={10}>{selected.PublishedBy || '-'}</Col>
                </Row>
              )}
              <Row>
                <Col xs={12} lg={6}>
                  <Form.Label className={s.fieldLabel}>
                    <FormattedMessage id="companionConfigVersion.attachZipFile" defaultMessage="Attach zip file" />
                  </Form.Label>
                  {isPublished ? (
                    // A published row is immutable (BCC-08) - the backend's
                    // update WHERE clause only ever touches Draft rows, so
                    // there's no delete affordance here (showDelete={false}),
                    // just a downloadable link via the same FileList used by
                    // firmware's own file list.
                    displayFileName ? (
                      <FileList files={[{ FileId: currentFileGuid, Name: displayFileName }]} showDelete={false} />
                    ) : (
                      <p className={s.fileAttached}>-</p>
                    )
                  ) : selected.VersionNumber ? (
                    <>
                      <FileUploader
                        widgetCode={widgetCode}
                        allowedFileTypes={ALLOWED_FILE_TYPES}
                        location={`config/${ModelId}/companionconfig/${saveGuid}`}
                        enableMultipleUpload={false}
                        onUploadComplete={onUploadComplete}
                        onUploadStart={onUploadStart}
                        onUploadFinished={onUploadFinished}
                      />
                      {isUploading && (
                        <p className={s.uploadWarning}>
                          <FormattedMessage
                            id="companionConfigVersion.uploadInProgressWarning"
                            defaultMessage="Uploading - do not close this tab or navigate away"
                          />
                        </p>
                      )}
                      {!isUploading && attachedFileName && (
                        <p className={s.fileAttached}>
                          {attachedFileName} <FormattedMessage id="companionConfigVersion.fileAttached" defaultMessage="attached" />
                        </p>
                      )}
                      {/* After the dropzone, not before - matches firmware's
                          own order (Attach File control, then the Firmware
                          Files list below it). */}
                      {IsEdit && displayFileName && (
                        <>
                          <p className={s.currentFileNotice}>
                            <FormattedMessage id="companionConfigVersion.currentFile" defaultMessage="Current file" />
                          </p>
                          <FileList files={[{ FileId: currentFileGuid, Name: displayFileName }]} onRemoveFile={onRemoveAttachedFile} />
                          <p className={s.currentFileNotice}>
                            <FormattedMessage id="companionConfigVersion.replaceFile" defaultMessage="Uploading a new file will replace the current one" />
                          </p>
                        </>
                      )}
                    </>
                  ) : (
                    // Disabled mimic of FileUploader's own "preUpload" look
                    // (same icon/copy/button) rather than a real disabled
                    // FileUploader instance - the shared component has no
                    // disabled prop, and adding one is out of scope here.
                    <div className={s.uploadPlaceholder} aria-disabled="true">
                      <img src={uploadFileIcon} alt="icon" className={s.uploadPlaceholderIcon} />
                      <div className={s.uploadPlaceholderText}>
                        <FormattedMessage id="fileUpload.dragndrop" defaultMessage="Drag and drop file here" />
                      </div>
                      <div className={s.uploadPlaceholderOr}>
                        <FormattedMessage id="fileUpload.or" defaultMessage="or" />
                      </div>
                      <Button variant="outline-secondary" className={s.uploadPlaceholderButton} disabled>
                        <FormattedMessage id="fileUpload.browse" defaultMessage="BROWSE FILE" />
                      </Button>
                      <div className={s.uploadPlaceholderFormats}>
                        <FormattedMessage id="fileUpload.acceptedfile" defaultMessage="Accepted file formats:" />{' '}
                        {ALLOWED_FILE_TYPES.map(type => type[1]).join(',')}
                      </div>
                      <div className={s.uploadPlaceholderHint}>
                        <FormattedMessage id="companionConfigVersion.enterVersionFirst" defaultMessage="Enter a version number to attach a file" />
                      </div>
                    </div>
                  )}
                  {!isPublished && localformErrors && localformErrors.ConfigFileS3Key && (
                    <p role="alert" className={s.error}>
                      {localformErrors.ConfigFileS3Key}
                    </p>
                  )}
                </Col>
              </Row>
              <div className={s.formButtons}>
                {!isPublished && (
                  <Button
                    variant="primary"
                    className={classNames(s.margin5, s.btnSaveChanges)}
                    onClick={submitForm}
                    noValidate
                    data-unittest="saveData"
                  >
                    <FormattedMessage id="companionConfigVersion.save" defaultMessage="SAVE" />
                  </Button>
                )}
                <Button variant="outline-secondary" className={s.btnCancel} onClick={onCancelClick}>
                  <FormattedMessage id="companionConfigVersion.cancel" defaultMessage="CANCEL" />
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </ModelConfigurationContentTemplate>
    </div>
  );
};

CompanionConfigVersion.propTypes = {
  saveCompanionConfigVersion: PropTypes.func.isRequired,
  describeCompanionConfigVersion: PropTypes.func.isRequired,
  changeCompanionConfigVersion: PropTypes.func.isRequired,
  resetCompanionConfigVersionStatus: PropTypes.func.isRequired,
  companionConfigVersionManager: PropTypes.object.isRequired,
  userProfileWidgets: PropTypes.object.isRequired
};

export default injectIntl(CompanionConfigVersion);
